import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { Labels, LegendWithValueOneLineBullet } from "@similarweb/ui-components";

import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { translate } from "app/filters/i18nFilter";
import { TrafficShareWithTooltip } from "pro-features/components/TrafficShare/src/TrafficShareWithTooltip";
import { FlexRowFlexStart } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { NoDataContainer } from "../../summary/intent-distribution/IntentDistributionStyledComponents";
import { SERP_TRAFFIC_DISTRIBUTION_API, SERP_TRAFFIC_DISTRIBUTION_QUERY_KEY } from "../consts";
import type { SerpTrafficDistributionResponse } from "../types";

import {
    LegendWrapper,
    SERPTrafficDistributionLegendsWrapper,
    SERPTrafficDistributionWrapper,
} from "./styledComponents";
import { getTrafficDistributionBarConfigs, transformSerpTrafficDistributionResponse } from "./utils";

const getLegendItems = (itemsData: { id: string; value: string; color: string; name: string }[]) =>
    itemsData?.map((item) => (
        <LegendWrapper>
            <LegendWithValueOneLineBullet
                text={item.name}
                value={item.value}
                isChecked={true}
                dataLabel={Labels.Bullet}
                labelColor={item.color}
            />
        </LegendWrapper>
    ));

export const SERPTrafficDistribution = () => {
    const { country, duration, webSource, pageFilter: pageFilterJson, key } = useCurrentStateAndParams().params;

    const { from, to, isWindow } = DurationService.getDurationData(duration).forAPI;

    const apiParams = {
        country,
        webSource,
        keys: key,
        pageFilterJson,
        from,
        to,
        isWindow,
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: [SERP_TRAFFIC_DISTRIBUTION_QUERY_KEY, apiParams],
        queryFn: () => {
            return DefaultFetchService.getInstance().get<SerpTrafficDistributionResponse>(
                SERP_TRAFFIC_DISTRIBUTION_API,
                apiParams,
            );
        },
        select: transformSerpTrafficDistributionResponse,
    });
    const hasData = data?.some((item) => item.rawValue > 0);
    const noDataObject = isError
        ? {
              title: translate("keyword.research.search.ads.table.error.title"),
              subtitle: translate("keyword.research.search.ads.table.error.subtitle"),
          }
        : {
              title: translate("common.noresults"),
              subtitle: translate("keyword.research.search.ads.table.no.data.subtitle"),
          };

    return (
        <WidgetContainer
            data-automation="serp-traffic-distribution"
            minWidth="100%"
            withBorder={true}
            title={translate("website.analysis.seo.overview.serp.traffic.distribution.title")}
            tooltip={translate("website.analysis.seo.overview.serp.traffic.distribution.tooltip")}
            isLoading={isLoading}
            loaderWidth="100%"
            noData={!isLoading && (!hasData || isError) ? true : false}
            noDataTitle={noDataObject.title}
            noDataSubtitle={noDataObject.subtitle}
            LoaderComponent={
                <NoDataContainer
                    width="100%"
                    height="24px"
                />
            }
        >
            <SERPTrafficDistributionWrapper gap={0}>
                <SERPTrafficDistributionLegendsWrapper>{getLegendItems(data)}</SERPTrafficDistributionLegendsWrapper>
                <FlexRowFlexStart>
                    {hasData ? (
                        <TrafficShareWithTooltip
                            title={translate("website.analysis.seo.overview.serp.traffic.distribution.title")}
                            data={getTrafficDistributionBarConfigs(data)}
                            trafficShareHeight={25}
                            minWidthToShowData={2}
                            minWidthToShowDataMiddle={2}
                        />
                    ) : null}
                </FlexRowFlexStart>
            </SERPTrafficDistributionWrapper>
        </WidgetContainer>
    );
};
